import React, { useMemo, useState } from 'react'
import { object, string } from 'prop-types'
import styles from './EventAgenda.module.scss'
import Type from '@/component/Primitive/Type'
import Container from '@/component/Primitive/Container'
import Stack from '@/component/Primitive/Stack'
import { Highlight } from '@/component/Primitive/Events'
import { Timeline } from './components'
import ButtonStandard from '@/component/Primitive/ButtonStandard'
import moment from 'moment'
import cx from 'classnames'

export const EventAgenda = ({ event, backgroundImage, theme, ...other }) => {
  const { startDate, endDate, agenda, locations } = event

  const formattedStartDate = moment(startDate).format('dddd Do MMMM')
  const formattedEndDate = moment(endDate).format('dddd Do MMMM')

  const dateRange =
    formattedStartDate === formattedEndDate
      ? `📅 ${formattedStartDate}`
      : `📅 ${formattedStartDate} - ${formattedEndDate}`

  const formatDate = (dateString) => moment(dateString).format('D.M.YYYY')
  const dayLabels = useMemo(
    () =>
      Array.from(new Set(agenda.map((item) => formatDate(item.startDate)))).map(
        (_, index) => 'Day ' + (index + 1)
      ),
    [agenda]
  )

  const sortedAgenda = useMemo(() => {
    return agenda.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
  }, [agenda])

  const agendaItemDayMap = useMemo(() => {
    const formattedStartDates = Array.from(
      new Set(agenda.map((item) => formatDate(item.startDate)))
    )
    return agenda.reduce((acc, curr) => {
      const formattedStartDate = formatDate(curr.startDate)
      if (!acc[formattedStartDate]) {
        acc[formattedStartDate] = {
          dayIndex:
            formattedStartDates.findIndex(
              (date) => formattedStartDate === date
            ) + 1,
          stages: []
        }
      } else {
        acc[formattedStartDate].dayIndex =
          formattedStartDates.findIndex((date) => formattedStartDate === date) +
          1
      }

      acc[formattedStartDate].stages.push(curr.location.name)
      acc[formattedStartDate].stages = Array.from(
        new Set(acc[formattedStartDate].stages)
      ).sort((a, b) => {
        const aI = locations.findIndex((loc) => loc.name === a)
        const bI = locations.findIndex((loc) => loc.name === b)
        return aI - bI
      })
      return acc
    }, {})
  }, [agenda, locations])

  const [selectedDay, setSelectedDay] = useState(() => {
    // If today is not between the first and last day, select the first day
    // Else select today
    const now = moment(new Date())
    if (
      now.isBetween(
        moment(sortedAgenda[0].startDate),
        moment(sortedAgenda[sortedAgenda.length - 1].endDate),
        'day',
        '[]'
      )
    ) {
      const formattedDate = formatDate(now)
      if (Object.keys(agendaItemDayMap).includes(formattedDate))
        return 'Day ' + agendaItemDayMap[formattedDate].dayIndex
      else return 'Day 1'
    } else {
      return (
        'Day ' +
        agendaItemDayMap[formatDate(sortedAgenda[0].startDate)].dayIndex
      )
    }
  })

  return (
    <div className={cx(styles.EventAgenda, theme === 'dark' && styles.dark)}>
      {backgroundImage && (
        <div
          className={styles.EventAgendaImage}
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}
      <Container className={styles.Container} center gutter size="wide">
        <Stack>
          <Stack gap="small">
            <div className={styles.Top}>
              <Stack gap="small">
                <Type
                  as="h1"
                  size={['heading5', 'heading2']}
                  weight="extrabold"
                  className={styles.Title}
                >
                  <Highlight>{`<h1>Event <strong>Agenda</strong></h1>`}</Highlight>
                </Type>
                <Type
                  as="h2"
                  size={['body2', 'body3']}
                  weight="medium"
                  className={styles.Subtitle}
                >
                  {dateRange}
                </Type>
              </Stack>
              <div className={styles.ButtonGroup}>
                {dayLabels.map((label) => (
                  <ButtonStandard
                    key={label}
                    primary
                    size="small"
                    onClick={() => setSelectedDay(label)}
                    className={cx(
                      styles.Button,
                      selectedDay === label && styles.active
                    )}
                  >
                    {label}
                  </ButtonStandard>
                ))}
              </div>
            </div>
            <Timeline
              event={event}
              sortedAgenda={sortedAgenda}
              agendaItemDayMap={agendaItemDayMap}
              selectedDay={selectedDay}
            />
          </Stack>
        </Stack>
      </Container>
    </div>
  )
}

EventAgenda.propTypes = {
  event: object,
  startDate: string,
  backgroundImage: object,
  theme: string
}

const ChannelItem = ({ name }) => {
  return (
    <Type size="body3" className={styles.ChannelItem}>
      <p className={styles.Text}>{name}</p>
    </Type>
  )
}

ChannelItem.propTypes = {
  name: string
}
